import { Skeleton } from '@mui/material';
import { FC } from 'react';

type ProfileSubpageStateProps = {
  title: string;
  isLoading?: boolean;
  isError?: boolean;
  errorMessage?: string;
  onRetry?: () => void;
  isRetrying?: boolean;
};

const ProfileSubpageState: FC<ProfileSubpageStateProps> = ({
  title,
  isLoading,
  isError,
  errorMessage = 'Something went wrong loading this section. Please try again.',
  onRetry,
  isRetrying = false,
}) => {
  if (isLoading) {
    return (
      <div className="px-6 md:px-0 md:min-w-[772px]" aria-busy="true">
        <Skeleton variant="text" width={280} height={42} />
        <Skeleton
          variant="text"
          width="80%"
          height={24}
          style={{ marginTop: 8 }}
        />
        <div className="mt-8 flex flex-col gap-6">
          {[1, 2, 3, 4].map(item => (
            <div key={item}>
              <Skeleton variant="text" width={140} height={20} />
              <Skeleton
                variant="rectangular"
                height={56}
                style={{ marginTop: 12, borderRadius: 8 }}
              />
            </div>
          ))}
        </div>
        <span className="sr-only">Loading {title}</span>
      </div>
    );
  }

  if (isError) {
    return (
      <div
        className="px-6 md:px-0 md:min-w-[772px]"
        role="alert"
        aria-live="polite"
      >
        <h1 className="text-2xl md:text-3xl font-semibold text-neutral-800">
          {title}
        </h1>
        <p className="mt-4 text-base text-neutral-500">{errorMessage}</p>
        {onRetry && (
          <button
            type="button"
            className="mt-6 rounded-lg bg-brand-secondary px-6 py-3 text-neutral-00 disabled:cursor-not-allowed disabled:opacity-60"
            onClick={onRetry}
            disabled={isRetrying}
            aria-label={`Retry loading ${title}`}
          >
            {isRetrying ? 'Retrying…' : 'Retry'}
          </button>
        )}
      </div>
    );
  }

  return null;
};

export default ProfileSubpageState;
